import { Injectable, inject } from '@angular/core';

import { Observable, Subject, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { EnvironmentConfig } from '../../../client-config/index';
import { NAVPOINTS, SPORT_CATEGORIES } from '../../../shared/constants/sports.constant';
import { BonusSuppressionService } from '../BonusSuppression/bonus-suppression.service';
import { CmsService } from '../cms/cms.service';
import { INavigationPoint } from '../cms/models';
import {
    BrandHeightConfig,
    ComponentHeightProperties,
    IItem,
    ILayoutStructureModel,
    LayoutForSportResponse,
    LayoutResults,
    Module,
    PlatformHeightConfig,
    componentHeights,
} from '../cms/models/layout/layout-structure';
import { PubSubService } from '../communication/pubsub/pubsub.service';
import { DeviceService } from '../device/device.service';
import { WindowRefService } from '../windowRef/window-ref.service';

@Injectable()
export class LayoutHelperService {
    private readonly environment = inject(EnvironmentConfig).configuration;
    private brand: string = this.environment.BRAND === 'bma' ? 'coral' : 'ladbrokes'; // "Ladbrokes" or "Coral"
    private platform: string = this.environment.CURRENT_PLATFORM ?? ''; // "desktop" or "mobile"
    private processedCls$: Subject<boolean> = new Subject<boolean>();
    window: any;
    constructor(
        private cmsService: CmsService,
        protected windowRefService: WindowRefService,
        private bonusSuppressionService: BonusSuppressionService,
        protected pubsub: PubSubService,
        private device: DeviceService,
    ) {
        this.window = this.windowRefService.nativeWindow;
    }

    public initLoad(): void {
        if (!this.device.isDesktop) {
            this.publishLayoutData();
            this.validateNavPoints();
        }
    }

    private publishLayoutData(): void {
        const pathName = this.window?.location?.pathname;
        let sportName: string = pathName?.split('/').pop() ?? '';
        sportName = sportName === 'featured' && pathName === '/horse-racing/featured' ? NAVPOINTS.horseracing : sportName;
        const sportId: string = NAVPOINTS[sportName];
        this.getLayoutForSport(sportId)?.subscribe(
            (layout) => {
                this.windowRefService.nativeWindow.clsLayoutData = layout;
                this.processedCls$.next(true);
            },
            (err) => {
                this.processedCls$.next(true);
            },
        );
    }

    // Getter to access the cls processed observable
    get isClsProcessed$(): Observable<boolean> {
        return this.processedCls$.asObservable();
    }

    /**
     * Check if the current route is valid for the cls sports
     * @returns boolean
     */
    private isRouteValid(): boolean {
        const pathname = this.windowRefService.nativeWindow.location.pathname;
        const validRoutes: string[] = [NAVPOINTS.footballsport, NAVPOINTS.tennissport, NAVPOINTS.horseracing];
        return validRoutes.some((route: string) => pathname.includes(route));
    }

    /**
     * Check if the current route support cls sports
     * Get all the navigation points for the cls sports
     * Validate for bonus supression
     * set the navigation Points data
     */
    validateNavPoints(): void {
        if (this.isRouteValid()) {
            this.cmsService
                .getNavigationPoints(
                    [NAVPOINTS[NAVPOINTS.footballsport], NAVPOINTS[NAVPOINTS.tennissport], NAVPOINTS[NAVPOINTS.horseracing]],
                    NAVPOINTS.type,
                )
                .subscribe((item: INavigationPoint[]) => {
                    const data: INavigationPoint[] = item.filter((point) => {
                        return this.bonusSuppressionService.checkIfYellowFlagDisabled(point.title);
                    });
                    this.windowRefService.nativeWindow.navPointsData = data;
                });
        }
    }
    /**
     * Fetch layout data for a specific sport and calculate component heights.
     * @param sportId The ID of the sport to fetch the layout for.
     * @returns An observable with the calculated min-heights and layout data.
     */
    getLayoutForSport(sportId: string): Observable<LayoutForSportResponse | boolean> {
        if (SPORT_CATEGORIES.includes(sportId)) {
            return this.cmsService.getFSCLayoutStructure(sportId).pipe(
                map((layoutData) => {
                    const minHeights = this.calculateStaticComponentHeights(layoutData);
                    return {
                        layoutData,
                        minHeights,
                    };
                }),
            );
        } else {
            return of(true);
        }
    }

    /**
     * Calculates the heights of all static and dynamic components based on the
     * layout data and configuration for the current brand and platform.
     * @param layoutData The layout data retrieved from the CMS service.
     * @returns An object containing the heights of all components in pixels.
     */
    private calculateStaticComponentHeights(layoutData: ILayoutStructureModel): LayoutResults {
        const results: LayoutResults = {
            header: this.getHeight('header'),
            switchers: this.getHeight('switchers'),
            matchResult: this.getHeight('matchResult'),
        };

        layoutData.modules.forEach((module: Module) => {
            const moduleName: string =
                layoutData.pageId === NAVPOINTS[NAVPOINTS.horseracing] && module.racingType ? module.racingType : (module as any)['@type'] || '';

            if (moduleName) {
                results[moduleName as keyof typeof results] = this.calculateDynamicComponentHeight(moduleName, this.brand, this.platform, module);
            }
        });

        return results;
    }

    /**
     * Calculates the height of a dynamic component based on the number of items,
     * brand, platform, and the component's height calculation method.
     * @param componentName The name of the component for which the height is calculated.
     * @param brand The brand (e.g., Ladbrokes, Coral).
     * @param platform The platform (e.g., mobile, desktop).
     * @param module The module data (e.g., RacingEventsModule, VirtualRaceModule).
     * @returns The calculated height of the component in pixels.
     */
    private calculateDynamicComponentHeight(componentName: string, brand: string, platform: string, module: Module): number {
        /**
         * Since componentHeights is imported at the top of the service,
         * it is executed only once when the service is initialized.
         * Each time you call getHeight, it refers to the existing componentHeights object,
         * which means it's not being recreated or recalculated on every call.
         */
        const componentConfig =
                componentHeights[componentName as keyof typeof componentHeights]?.[brand as keyof BrandHeightConfig]?.[
                    platform as keyof PlatformHeightConfig
                ],
            items: number = module.noOfItems,
            noOfItemsList: { typeName: string; count: number; items: Array<{ earlyPriceAvailable?: boolean }> }[] = module.noOfItemsList || [];

        if (!componentConfig) {
            console.warn(`No configuration found for ${componentName}`);
            return 0;
        }

        const {
            calculationMethod,
            headerHeight,
            subHeaderHeight = 0,
            sectionHeight = 0,
            switcherHeight = 0,
            meetingHeight = 0,
            earlypriceHeight = 0,
        } = componentConfig;

        // Cache the base height for this component
        const baseHeight = this.getHeight(componentName, componentConfig) || 0;

        switch (calculationMethod) {
            case 'static':
                return baseHeight + headerHeight;

            case 'perItem':
                return baseHeight * (items as number) + headerHeight;

            case 'perHalfSize':
                return baseHeight * Math.ceil((items as number) / 2) + headerHeight;

            case 'perLeague':
                //Special calculation for hierarchical data
                const leagues = noOfItemsList;
                const totalEvents = (leagues && leagues.length && leagues.reduce((sum, league) => sum + league.count, 0)) || 0;
                return headerHeight + subHeaderHeight * (leagues && leagues.length ? leagues.length : 0) + baseHeight * totalEvents;
            //return 400;
            case 'perRaceContent':
                return baseHeight + (module.filtersEnabled ? headerHeight : 0) + subHeaderHeight + items * sectionHeight;
            case 'perMeeting':
                let groupedRacingLength: number = 0;
                noOfItemsList?.forEach((classType: { typeName: string; count: number; items: Array<{ earlyPriceAvailable?: boolean }> }) => {
                    let allMeetingsHeight: number = 0;
                    classType?.items?.forEach((item: IItem) => {
                        allMeetingsHeight += subHeaderHeight + meetingHeight + (item.earlyPriceAvailable ? earlypriceHeight : 0);
                    });
                    groupedRacingLength += baseHeight + headerHeight + switcherHeight + allMeetingsHeight;
                });
                return groupedRacingLength;
            default:
                return baseHeight + headerHeight;
        }
    }

    /**
     * Retrieves the base height of a static component based on brand and platform.
     * Supports dynamic adjustments based on the unit of measurement.
     * @param componentName The name of the component for which the height is needed.
     * @param heightConfig Optionally, the height configuration can be passed directly.
     * @returns The calculated height of the component in pixels, converted if necessary.
     */
    private getHeight(componentName: string, heightConfig?: ComponentHeightProperties): number {
        // If no specific height configuration is passed, use the default configuration for the component.
        if (!heightConfig) {
            heightConfig =
                componentHeights[componentName as keyof typeof componentHeights]?.[this.brand as keyof BrandHeightConfig]?.[
                    this.platform as keyof PlatformHeightConfig
                ];
        }

        if (!heightConfig) {
            console.warn(`No height configuration found for ${componentName}`);
            return 0;
        }

        const { baseHeight, unit } = heightConfig;
        let totalHeight = baseHeight;

        // Convert the base height based on the specified unit of measurement.
        switch (unit) {
            case 'vw':
                totalHeight = this.vwToPx(baseHeight);
                break;
            default:
                return totalHeight;
        }
        return totalHeight;
    }

    /**
     *
     * @param vw takes input in vw
     * @returns number after converting to px
     */
    private vwToPx(vw: number): number {
        const viewportWidth = window.innerWidth;
        return (vw * viewportWidth) / 100;
    }
}
